// ─────────────────────────────────────────────────────────────────────────────
// Extractor Studio design tokens — the canonical set from the DocuSensa
// "Extractor Studio — Design Tokens" frontend handoff.
//
// Scoped to Studio/ on purpose. The legacy Extractor flow (TemplatesDashboard /
// TemplateWorkspace / TemplateBuilder) still runs on the older #8B55DD palette
// via ../constants CLS, and stays the fallback behind USE_STUDIO — so Studio
// carries its own token set rather than mutating the shared one.
//
// Tailwind's JIT only sees class names that appear literally in source, so
// every class below is written out in full (no runtime string building).
// ─────────────────────────────────────────────────────────────────────────────

/* ── 00 · Foundations ─────────────────────────────────────────────────────── */
export const ES = {
  primary: "#A04A97",
  primaryHover: "#893F82",
  primarySoft: "#F7EBF5",
  primaryTint: "#FBF5FA",
  primaryBorder: "#E9D2E6",

  sidebar: "#2C1338",
  surface: "#FFFFFF",
  canvas: "#FAF9FB",
  hover: "#F5F5F5",

  ink: "#1A1A1A",        // titles, field names, active labels
  sub: "#52525B",        // page headers, sub-copy, row meta
  tert: "#8A8A90",       // muted labels, counts, placeholders
  line: "#ECE9EE",       // card borders, dividers, input strokes

  success: "#2F7D44",
  warning: "#C77A1C",
  danger: "#B33A1E",
};

/* Elevation & radius (handoff §00). Popovers and menus share one shadow;
   primary CTAs are SOLID — the handoff explicitly rules out gradients. */
export const SHADOW = {
  popover: "shadow-[-2px_4px_20px_6px_rgba(0,0,0,0.10)]",
  cta: "shadow-[0_2px_6px_rgba(0,0,0,0.08)]",
  card: "shadow-[0_6px_18px_rgba(0,0,0,0.07)]",
};

/* ── Type scale — Outfit (handoff §00) ────────────────────────────────────── */
export const TX = {
  pageTitle: "text-[20px] font-semibold text-[#52525B]",
  screenName: "text-[16.5px] font-semibold text-[#1A1A1A]",
  sectionHeading: "text-[16px] font-semibold text-[#1A1A1A]",
  cardTitle: "text-[14.5px] font-semibold text-[#1A1A1A]",
  rowTitle: "text-[13.5px] font-semibold text-[#1A1A1A]",
  body: "text-[13.5px] font-medium text-[#1A1A1A]",
  helper: "text-[12.5px] font-normal text-[#52525B]",
  meta: "text-[11.5px] font-medium text-[#8A8A90]",
  overline: "text-[11px] font-semibold uppercase tracking-[0.06em] text-[#8A8A90]",
};

/* ── Component classes ────────────────────────────────────────────────────── */
export const SX = {
  // Buttons — 13–13.5 / 600, radius 8–10px.
  btn:
    "inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[9px] text-[13px] font-semibold " +
    "whitespace-nowrap cursor-pointer transition-all duration-150 " +
    "disabled:opacity-40 disabled:cursor-not-allowed",
  btnPrimary:
    "bg-[#A04A97] text-white border-none shadow-[0_2px_6px_rgba(0,0,0,0.08)] " +
    "hover:enabled:bg-[#893F82]",
  btnSecondary:
    "bg-white text-[#1A1A1A] border border-[#ECE9EE] hover:enabled:bg-[#F5F5F5]",
  btnGhost:
    "bg-white text-[#52525B] border border-[#ECE9EE] " +
    "hover:enabled:bg-[#F5F5F5] hover:enabled:text-[#1A1A1A]",
  btnSoft:
    "bg-white text-[#893F82] border border-[#E9D2E6] hover:enabled:bg-[#FBF5FA]",
  btnSm: "!text-[12px] !px-2.5 !py-1.5",

  // Inputs — #A04A97 stroke on hover/focus + 3px tint ring on focus.
  input:
    "w-full bg-white border border-[#ECE9EE] rounded-[9px] px-3 py-2.5 text-[13px] text-[#1A1A1A] " +
    "outline-none transition-all duration-150 placeholder:text-[#8A8A90] " +
    "hover:border-[#A04A97] focus:border-[#A04A97] focus:shadow-[0_0_0_3px_rgba(160,74,151,0.14)]",
  inputMono: "font-mono",

  // Surfaces
  card: "bg-white border border-[#ECE9EE] rounded-[14px]",
  cardSm: "bg-white border border-[#ECE9EE] rounded-[11px]",
  cardHover:
    "transition-all duration-150 hover:border-[#E9D2E6] hover:shadow-[0_6px_18px_rgba(0,0,0,0.07)]",
  popover:
    "bg-white border border-[#ECE9EE] rounded-[13px] shadow-[-2px_4px_20px_6px_rgba(0,0,0,0.10)]",
  divider: "bg-[#ECE9EE]",

  // Chrome — top bar is 64px tall with 28px side padding on every screen.
  topbar:
    "flex items-center h-16 px-7 bg-white border-b border-[#ECE9EE] flex-shrink-0",

  // Badges / pills — radius 999.
  badge:
    "inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10.5px] font-semibold",
};

/* ── Layout frames (handoff §02–05) ───────────────────────────────────────────
   The one intentional variance across screens is content width; every screen
   shares the 24px canvas inset from the chrome. */
export const FRAME = {
  canvas: "bg-[#FAF9FB]",
  canvasPad: "p-6",                                  // 24px inset
  builder: "max-w-[960px] mx-auto mt-5",             // 02 · Template Builder
  library: "max-w-[1340px] mx-auto",                 // 03 · Library
  templateView: "max-w-[980px] mx-auto px-6 pt-[26px] pb-12", // 04 · Template View
  blockGap: "gap-7",                                 // 28px between builder blocks
};

/* Test screen (05) column defaults — full-width, drag-resizable, collapsible. */
export const TEST_PANES = { left: 300, centerMin: 380, right: 320 };

/* Category badge colours — shared by Library rows, recent cards and the
   template header (handoff §00). */
export const CATEGORY_BADGE = {
  invoice: "bg-[#EEF4FD] text-[#2F5FA8]",
  purchase_order: "bg-[#EEF9F0] text-[#2F7D44]",
  contract: "bg-[#FDF3E7] text-[#A8662F]",
  receipt: "bg-[#F5EEFB] text-[#7B3FA8]",
  custom: "bg-[#F3F3F3] text-[#6B6B6B]",
};
